import React, { useState, useCallback, useEffect } from "react";
import Widget from "../Widget";
import Request from "../../../../utils/fetchUtils";
import Loader from "../Loader";
import { getSessionObject } from "../../../../utils/sessionUtils.js";
import "../../../../styles/fees-manager.css";

const FeesList = () => {
  const [fees, setFees] = useState([]);
  const [filteredFees, setFilteredFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [availableQuarters, setAvailableQuarters] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedQuarterYear, setSelectedQuarterYear] = useState('');

  let data = [];

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const { role } = getSessionObject();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatClientId = (clientId) => {
    const emailMatch = clientId.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
    return emailMatch ? emailMatch[0] : clientId;
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNumber - 1] || monthNumber;
  };

  const getQuarter = (month) => {
    if (month >= 1 && month <= 3) return 'Q1';
    if (month >= 4 && month <= 6) return 'Q2';
    if (month >= 7 && month <= 9) return 'Q3';
    if (month >= 10 && month <= 12) return 'Q4';
    return null;
  };

  const getUniqueQuarters = (feesData) => {
    const quarterYearPairs = feesData.map(fee => ({
      quarter: getQuarter(fee.month),
      year: fee.year,
      key: `${getQuarter(fee.month)}-${fee.year}`
    })).filter(item => item.quarter !== null);

    const uniqueQuarters = [...new Map(quarterYearPairs.map(item => [item.key, item])).values()];
    
    return uniqueQuarters.sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.quarter.localeCompare(a.quarter);
    });
  };

  // Función para ordenar fees del más reciente al más viejo
  const sortFeesByDate = (fees) => {
    return [...fees].sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.month - a.month;
    });
  };

  // Función para obtener años únicos de los datos
  const getUniqueYears = (feesData) => {
    const years = [...new Set(feesData.map(fee => fee.year))];
    return years.sort((a, b) => b - a);
  };

  const getUniqueMonths = (feesData, year = null) => {
    let filteredData = feesData;
    if (year) {
      filteredData = feesData.filter(fee => fee.year === parseInt(year));
    }
    const months = [...new Set(filteredData.map(fee => fee.month))];
    return months.sort((a, b) => b - a);
  };

  // Función para filtrar fees por año y mes
  const filterFees = useCallback((feesData, year, month) => {
    let filtered = feesData;

    if (year && year !== '') {
      filtered = filtered.filter(fee => fee.year === parseInt(year));
    }

    if (month && month !== '') {
      filtered = filtered.filter(fee => fee.month === parseInt(month));
    }

    return sortFeesByDate(filtered);
  }, []);

  // Manejar cambio de año
  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedMonth('');
    
    if (year && year !== '') {
      const monthsForYear = getUniqueMonths(fees, year);
      setAvailableMonths(monthsForYear);
    } else {
      setAvailableMonths(getUniqueMonths(fees));
    }

    const filtered = filterFees(fees, year, '');
    setFilteredFees(filtered);
  };

  // Manejar cambio de mes
  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    const filtered = filterFees(fees, selectedYear, month);
    setFilteredFees(filtered);
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedQuarter('');
    setSelectedQuarterYear('');
    setAvailableMonths(getUniqueMonths(fees));
    setFilteredFees(sortFeesByDate(fees));
  };

  const handleFetchData = async () => {
    const sess = JSON.parse(localStorage.session);
    const userId = sess?.userId;
    if (typeof userId === "string") {
      setLoading(true);
      const res = await Request(`trading/fees`);

      if (res.ok) {
        setLoading(false);
        data = res.data;
        setFees(data);
        setFilteredFees(sortFeesByDate(data));
        
        // Configurar opciones de filtro
        const years = getUniqueYears(data);
        const months = getUniqueMonths(data);
        const quarters = getUniqueQuarters(data);
        
        setAvailableYears(years);
        setAvailableMonths(months);
        setAvailableQuarters(quarters);

        // Establecer valores por defecto para el trimestre más reciente
        if (quarters.length > 0) {
          setSelectedQuarter(quarters[0].quarter);
          setSelectedQuarterYear(quarters[0].year);
        }
      }
    }
  };

  const downloadQuarterlyCSV = async (quarter, year) => {
    if (!quarter || !year) {
      console.error('Quarter and year are required');
      return;
    }

    setCsvLoading(true);
    try {
      const csvBlob = await Request(
        `trading/export-q?quarter=${quarter}&year=${year}`, 
        {}, 
        'GET', 
        {}, 
        false, 
        true 
      );

      if (csvBlob) {
        const url = window.URL.createObjectURL(csvBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `quarterly_fees_${quarter}_${year}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
    } finally {
      setCsvLoading(false);
    }
  };

  const fetch = useCallback(() => {
    handleFetchData();
  }, []);

  useEffect(() => {
    fetch();
  }, []);

  return (
    <Widget name={"fees"}>
      <div className="controls-container">
        {/* Sección de Filtros */}
        <div className="filters-section">
          <div className="filter-group">
            <label className="filter-label">Year:</label>
            <select 
              className="filter-select"
              value={selectedYear} 
              onChange={(e) => handleYearChange(e.target.value)}
            >
              <option value="">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

        <div className="filter-group">
          <label className="filter-label">
            Filter by Month:
          </label>
          <select
            className="filter-select" 
            value={selectedMonth} 
            onChange={(e) => handleMonthChange(e.target.value)}
          >
            <option value="">All Months</option>
            {availableMonths.map(month => (
              <option key={month} value={month}>{getMonthName(month)}</option>
            ))}
          </select>        </div>        <button
          onClick={clearFilters}
          className="reset-filters-btn"
        >
          Reset Filters
        </button>

          {/* Filtros activos */}
          {(selectedYear || selectedMonth) && (
            <div className="active-filters">
              {selectedYear && `${selectedYear}`}
              {selectedMonth && ` - ${getMonthName(parseInt(selectedMonth))}`}
              {` (${filteredFees.length})`}
            </div>
          )}
        </div>

        {/* Sección de Exportación - Solo visible para roles que NO sean customer */}
        {role !== "customer" && (
          <div className="export-section">
            <div className="filter-group">
              <label className="filter-label">Export Quarter:</label>
              <select 
                className="filter-select"
                value={`${selectedQuarter}-${selectedQuarterYear}`}
                onChange={(e) => {
                  const [quarter, year] = e.target.value.split('-');
                  setSelectedQuarter(quarter);
                  setSelectedQuarterYear(parseInt(year));
                }}
              >
                <option value="">Select Quarter</option>
                {availableQuarters.map(({ quarter, year, key }) => (
                  <option key={key} value={`${quarter}-${year}`}>
                    {quarter} {year}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => downloadQuarterlyCSV(selectedQuarter, selectedQuarterYear)}
              className="export-csv-btn"
              disabled={csvLoading || !selectedQuarter || !selectedQuarterYear}
            >
              {csvLoading ? 'Downloading...' : 'Download CSV'}
            </button>

            {/* Botones rápidos */}
            <div className="quick-export-buttons">
              <span className="quick-label">Quick:</span>
              {availableQuarters.slice(0, 3).map(({ quarter, year, key }) => (
                <button
                  key={key}
                  onClick={() => downloadQuarterlyCSV(quarter, year)}
                  className="quick-export-btn"
                  disabled={csvLoading}
                >
                  {quarter} {year}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {!loading ? (
        <>
          <table className="fees-management-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Month</th>
                <th>Year</th>
                <th>Quarter</th>
                <th>AUM</th>
                <th>Fee Percentage</th>
                <th>Monthly Fee</th>
                <th>Advisor Fee %</th>
                <th>Fund Fee %</th>
              </tr>
            </thead>
            <tbody>
              {filteredFees.map((fee, index) => (
                <tr key={fee.feeId || index}>
                  <td className="name first">
                    <div className="user">
                      <div>
                        <span className="name" title={fee.clientId}>
                          {formatClientId(fee.clientId)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="score">
                    <p className="score-text">{getMonthName(fee.month)}</p>
                  </td>
                  <td className="inv-amount">
                    <p className="score-text">{fee.year}</p>
                  </td>
                  <td className="inv-amount">
                    <p className="score-text">{getQuarter(fee.month)}</p>
                  </td>
                  <td className="inv-amount">
                    <p className="score-text">{formatCurrency(fee.aum)}</p>
                  </td>
                  <td className="inv-amount">
                    <p className="score-text">{formatPercentage(fee.feePercentage)}</p>
                  </td>
                  <td className="inv-amount">
                    <p className="score-text">{formatCurrency(fee.monthlyFee)}</p>
                  </td>
                  <td className="inv-amount">
                    <p className="score-text">{formatPercentage(fee.advisorFeePercentage)}</p>
                  </td>
                  <td className="inv-amount">
                    <p className="score-text">{formatPercentage(fee.fundFeePercentage)}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredFees.length === 0 && (
            <div className="no-results">
              No fees found for the selected filters.
            </div>
            )}
        </>
      ) : (
        <>
          <div className="heading">
            <h1 className="title">Fees</h1>
          </div>
          <Loader></Loader>
        </>
      )}
    </Widget>
  );
};

export default FeesList;